<i18n locale="en">
{
  "heading": {
    "title": "Subject",
    "location": "Subscriber audience"
  },
  "location": {
    "all-locations": "This campaign sends to all brand locations"
  },
  "footer": {
    "titles": {
      "scheduled": "Scheduled by you",
      "sent": "Sent by you"
    },
    "subtitle": "This campaign was designed by you."
  }
}
</i18n>

<template>
  <div v-if="!!campaign">
    <div class="otc-sidebar pa-10 flex-col gap-6">
      <!-- Controls Section -->
      <template v-if="canEdit">
        <CampaignControls
          v-model="action"
          :execute-at="executeAt"
          :campaign="campaign.id"
          :campaign-slug="campaign.slug"
          :email-template-id="emailTemplateId"
          :transport="campaign.transport ?? undefined"
          @show-dialog="showDialog = true"
        />
        <OwnRule type="dashed" />
      </template>

      <!-- Subject Section-->
      <template v-if="campaign.transport === 'email'">
        <TitleBlock
          :title="t('heading.title')"
          :subtitle="campaign.name"
          subtitle-color="primary"
        />
      </template>

      <!-- Location Section -->
      <template v-if="shouldShowLocationSection">
        <TitleBlock
          :title="t('heading.location')"
          :subtitle="locationSubtitle"
          subtitle-color="primary"
        />
      </template>

      <!-- Analytics Section-->
      <template v-if="!canEdit">
        <template v-if="formattedSendDate">
          <TitleBlock :title="'Sent on'" :subtitle="formattedSendDate" />
          <OwnRule type="dashed" />
        </template>

        <CampaignAnalytics
          :campaign="campaign.id"
          :transport="campaign.transport ?? undefined"
        />
        <OwnRule type="dashed" />
      </template>

      <TitleBlock
        :title="t(`footer.titles.${!canEdit ? 'sent' : 'scheduled'}`)"
        :subtitle="t('footer.subtitle')"
        subtitle-color="primary"
      />
    </div>

    <component
      :is="dialog"
      :action="action"
      :methods="methods"
      :show="showDialog"
      :store-module="storeModule"
      @close-dialog="showDialog = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { type Component } from 'vue'
import { useI18n } from 'vue-i18n'

import TitleBlock from '@/components/typography/TitleBlock.vue'
import { useActiveBrand } from '@/core/store'
import { type BuilderMethods } from '@/lib/builder/types'
import { useMappedGetter } from '@/store'
import { OwnRule } from '@/ui/OwnRule'
import { formatDateTimeWithTimezone } from '@/utils/date/formatDateTimeWithTimezone'

import { type CampaignResource } from '../../../builders/email/types'
import {
  BaseCampaignResource,
  CampaignBlock,
  MarketingStoreModule,
} from '../../../types'

import CampaignAnalytics from './CampaignAnalytics.vue'
import CampaignControls from './CampaignControls.vue'

const props = defineProps<{
  /**
   * Dialog component for carrying out some of the actions available in this component (e.g. editing subject, editing schedule).
   */
  dialog: Component
  storeModule: MarketingStoreModule
  methods: BuilderMethods<BaseCampaignResource, unknown, unknown, CampaignBlock>
}>()

const { t } = useI18n()

const action = ref(undefined)
const showDialog = ref(false)

const campaign = useMappedGetter<BaseCampaignResource>(
  `${props.storeModule}/currentResource`
)

const { locations: brandLocations, getLocationById } = useActiveBrand()

const locationSubtitle = computed(() => {
  if (campaign.value.locationId) {
    const location = getLocationById.value(campaign.value.locationId)
    const locationName = location?.name
    if (locationName) {
      return `Location: ${locationName}`
    }
  }
  return 'Location: All'
})

const shouldShowLocationSection = computed(
  () => brandLocations.value && brandLocations.value.length > 1
)

const emailTemplateId = computed(
  () => (campaign.value as CampaignResource | undefined)?.emailTemplateId
)
const canEdit = computed(
  () => !['active', 'completed'].includes(campaign.value.status)
)
const executeAt = computed(() =>
  campaign.value.executeAt ? new Date(campaign.value.executeAt) : undefined
)
const formattedSendDate = computed(() => {
  const { executeAt } = campaign.value
  if (!executeAt) return undefined

  const sendDate = new Date(executeAt)
  return formatDateTimeWithTimezone(sendDate)
})
</script>
